package twist.mena.sports.admin.components

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import twist.mena.sports.admin.api.AdminAdsApi
import twist.mena.sports.admin.theme.*
import twist.mena.sports.shared.dto.ads.AdResponse
import twist.mena.sports.shared.dto.ads.CreateAdRequest
import twist.mena.sports.shared.dto.ads.UpdateAdRequest

@Composable
fun AdDialog(
    ad: AdResponse?,
    api: AdminAdsApi,
    onSaved: () -> Unit,
    onClose: () -> Unit
) {
    val colors = AppTheme.colors
    val isEditMode = ad != null
    var adType by remember(ad) { mutableStateOf(ad?.adType ?: "BANNER") }
    var adLocation by remember(ad) { mutableStateOf(ad?.adLocation ?: "") }
    var iosAdId by remember(ad) { mutableStateOf(ad?.iosAdId ?: "") }
    var androidAdId by remember(ad) { mutableStateOf(ad?.androidAdId ?: "") }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var locationSuggestions by remember { mutableStateOf<List<String>>(emptyList()) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        try {
            locationSuggestions = api.getLocations()
        } catch (_: Exception) { }
    }

    Modal(
        title = if (isEditMode) "Edit Ad" else "Create Ad",
        isOpen = true,
        onClose = onClose
    ) {
        // Ad Type — Select dropdown
        FormField("Ad Type") {
            Select({
                onChange { adType = it.value ?: "BANNER" }
                style { formInputStyle(colors) }
                attr("value", adType)
            }) {
                Option("BANNER", { if (adType == "BANNER") attr("selected", "") }) { Text("Banner") }
                Option("INTERSTITIAL", { if (adType == "INTERSTITIAL") attr("selected", "") }) { Text("Interstitial") }
            }
        }

        // Ad Location — Input with datalist for suggestions
        FormField("Ad Location") {
            Input(InputType.Text) {
                value(adLocation)
                onInput { adLocation = it.value }
                attr("list", "ad-location-suggestions")
                attr("placeholder", "e.g. home_screen, match_detail")
                style { formInputStyle(colors) }
            }
            TagElement<org.w3c.dom.HTMLElement>("datalist", {
                id("ad-location-suggestions")
            }) {
                locationSuggestions.forEach { loc ->
                    Option(loc)
                }
            }
        }

        FormField("iOS Ad ID") {
            Input(InputType.Text) {
                value(iosAdId)
                onInput { iosAdId = it.value }
                attr("placeholder", "ca-app-pub-xxx/yyy")
                style { formInputStyle(colors) }
            }
        }

        FormField("Android Ad ID") {
            Input(InputType.Text) {
                value(androidAdId)
                onInput { androidAdId = it.value }
                attr("placeholder", "ca-app-pub-xxx/yyy")
                style { formInputStyle(colors) }
            }
        }

        // Error
        error?.let { msg ->
            Div({
                style { errorMessageStyle(colors) }
            }) { Text(msg) }
        }

        // Action buttons
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.End)
                gap(Spacing.md)
                marginTop(Spacing.xl)
            }
        }) {
            Button({
                onClick { onClose() }
                style { secondaryButtonStyle(colors) }
            }) { Text("Cancel") }

            Button({
                if (saving) disabled()
                onClick {
                    scope.launch {
                        saving = true
                        error = null
                        try {
                            if (isEditMode && ad != null) {
                                api.updateAd(
                                    ad.id,
                                    UpdateAdRequest(
                                        adType = adType,
                                        adLocation = adLocation,
                                        iosAdId = iosAdId,
                                        androidAdId = androidAdId
                                    )
                                )
                            } else {
                                api.createAd(
                                    CreateAdRequest(
                                        adType = adType,
                                        adLocation = adLocation,
                                        iosAdId = iosAdId,
                                        androidAdId = androidAdId
                                    )
                                )
                            }
                            onSaved()
                            onClose()
                        } catch (e: Exception) {
                            error = e.message ?: "Operation failed"
                        } finally {
                            saving = false
                        }
                    }
                }
                style { primaryButtonStyle(colors, disabled = saving) }
            }) { Text(if (saving) "Saving..." else if (isEditMode) "Save Changes" else "Create") }
        }
    }
}
